import { Box, SxProps, useTheme } from '@mui/material';
import { useState } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import { useImageFormats } from '../../hooks/useImageFormats';
import { StrapiImage } from '../../models/common';
import { StrapiImageFormat } from '../../models/image';
import { IconSearch } from '../icons/IconSearch';
import { IconButton } from './IconButton';
import { Image } from './Image';

type ImageStrapiProps = {
  strapiImage?: StrapiImage;
  isZoomable?: boolean;
  selectedFormat?: StrapiImageFormat;
  alt?: string;
  sx?: SxProps;
  /**
   * Aspect ratio in percentage
   * X : width, Y: height
   * X:Y = X / Y
   * 16:9 = 56.25%
   * 4:3 = 75%
   */
  aspectRatio?: string;
  ariaDescribedby?: string;
};

export const ImageStrapi = ({
  strapiImage,
  isZoomable = false,
  selectedFormat,
  alt,
  aspectRatio,
  sx = {},
  ariaDescribedby
}: ImageStrapiProps) => {
  const { getImageSrc } = useImageFormats();
  const strapiImageSrc = strapiImage ? getImageSrc(strapiImage, selectedFormat) : undefined;
  const alternativeText = alt ?? strapiImage?.alternativeText;
  if (!strapiImageSrc) return null;

  if (!isZoomable) {
    return (
      <Image
        src={strapiImageSrc}
        alt={alternativeText ?? ''}
        aspectRatio={aspectRatio}
        sx={{ width: '100%', ...sx }}
        ariaDescribedby={ariaDescribedby}
      />
    );
  }

  return (
    <Box
      sx={{
        position: 'relative'
      }}
    >
      <ZoomBox isZoomable={isZoomable} strapiImage={strapiImage} strapiAlt={alt}></ZoomBox>
      <Image
        src={strapiImageSrc}
        alt={alternativeText ?? ''}
        aspectRatio={aspectRatio}
        sx={{ width: '100%', ...sx }}
        ariaDescribedby={ariaDescribedby}
      />
    </Box>
  );
};

const ZoomBox = ({
  isZoomable,
  strapiImage,
  strapiAlt
}: {
  isZoomable?: boolean;
  strapiImage?: StrapiImage;
  strapiAlt?: string;
}) => {
  const theme = useTheme();
  const { getImageSrc } = useImageFormats();
  const [open, setOpen] = useState(false);
  if (!isZoomable) {
    return null;
  }

  const source = strapiImage ? getImageSrc(strapiImage, 'large', 2000) : undefined;
  const imageAlt = strapiAlt ?? '';

  return (
    <>
      <IconButton
        disableRipple
        aria-label="Zoom"
        sx={{
          position: 'absolute',
          bottom: '1rem',
          right: '1rem',
          '&:focus, &:focus-visible': {
            outline: `3px solid ${theme.palette.primary.main}`,
            outlineOffset: '1px',
            boxShadow: 'none'
          }
        }}
        iconButtonType="light"
        onClick={() => setOpen(true)}
      >
        <IconSearch />
      </IconButton>
      <Lightbox
        open={open}
        plugins={[Zoom]}
        zoom={{
          scrollToZoom: true,
          maxZoomPixelRatio: 5
        }}
        styles={{
          root: {
            '--yarl__color_button': theme.palette.primary.main,
            '--yarl__focus_outline': `3px solid ${theme.palette.primary.main}`,
            '--yarl__button:hover': 'white',
            '--yarl__button:focus': 'white',
            '--yarl__button:focus-visible': 'white',
            '--yarl__button_border:focus': `1px solid ${theme.palette.primary.main}`,
            '--yarl__button_border:active': `1px solid ${theme.palette.primary.main}`
          }
        }}
        close={() => setOpen(false)}
        render={{
          buttonPrev: () => null,
          buttonNext: () => null
        }}
        slides={[{ src: source ?? '', alt: imageAlt }]}
      />
    </>
  );
};
